import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { productSchema } from "@/lib/schema";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BookingPopupModal } from "@/components/home/BookingPopupModal";
import { 
  Users, 
  Clock, 
  Waves, 
  UtensilsCrossed, 
  Car, 
  CheckCircle, 
  Umbrella,
  Sun,
  Sparkles,
  TreePine,
  Music,
  Wifi,
  ShowerHead,
  Baby,
  Dog,
  Info,
  Check
} from "lucide-react";

// Images
import aerialCabanas from "@/assets/gallery/aerial-cabanas.jpg";
import aerialRiverCabanas from "@/assets/gallery/aerial-river-cabanas.jpg";
import riverCanopies from "@/assets/gallery/river-canopies.jpg";
import riverHangout from "@/assets/gallery/river-hangout.jpg";
import tikiLounge from "@/assets/gallery/tiki-lounge.jpg";
import grillingFood from "@/assets/gallery/grilling-food.jpg";
import kidsPlayingRiver from "@/assets/gallery/kids-playing-river.jpg";
import clearRiver from "@/assets/gallery/clear-river.jpg";
import cabanaHero from "@/assets/cabanas/cabana-hero.jpg";
import cabanasMultiple from "@/assets/cabanas/cabanas-multiple.jpg";
import cabanaRiverView from "@/assets/cabanas/cabana-river-view.jpg";
import kidsRiverTubes from "@/assets/cabanas/kids-river-tubes.jpg";
import tubingRiverCanyon from "@/assets/cabanas/tubing-river-canyon.jpg";
import womanWithTubes from "@/assets/cabanas/woman-with-tubes.jpg";
import tentCabanaCombo from "@/assets/cabanas/tent-cabana-combo.jpg";
import cabanaRiverside from "@/assets/cabanas/cabana-riverside.jpg";
import whatsIncludedImage from "@/assets/cabanas/whats-included.jpg";
import cabanaSeating from "@/assets/cabanas/cabana-seating.webp";
import friendsGroupCabana from "@/assets/cabanas/friends-group-cabana.jpg";
import cabanasRiverStairs from "@/assets/cabanas/cabanas-river-stairs.jpg";
import cabanasFloatingGuests from "@/assets/cabanas/cabanas-floating-guests.jpg";
import cabanaInteriorView from "@/assets/cabanas/cabana-interior-view.jpg";
import cabanasRiverViewWide from "@/assets/cabanas/cabanas-river-view.jpg";
import familyGroupCabana from "@/assets/cabanas/family-group-cabana.webp";
import riverTubingScene from "@/assets/cabanas/river-tubing-scene.jpg";
import cabanaGrillSeating from "@/assets/cabanas/cabana-grill-seating.jpg";
import cabanaInteriorRiverside from "@/assets/cabanas/cabana-interior-riverside.jpg";

const cabanaFeatures = [
  {
    icon: Umbrella,
    title: "Shaded Comfort",
    description: "Large covered cabana with ceiling fans to keep you cool all day"
  },
  {
    icon: Waves,
    title: "Private River Access",
    description: "Steps away from the crystal-clear Guadalupe River"
  },
  {
    icon: UtensilsCrossed,
    title: "Full Grill Setup",
    description: "Charcoal grill, picnic tables, and prep area included"
  },
  {
    icon: Car,
    title: "Dedicated Parking",
    description: "Your vehicle stays close — no shuttles or long walks"
  },
  {
    icon: Users,
    title: "Up to 12 Guests (8 included with cabana rental)",
    description: "Perfect for families, friend groups, or small gatherings"
  },
  {
    icon: Clock,
    title: "All-Day Access",
    description: "10:30 AM to 6:30 PM — enjoy the river at your own pace"
  }
];

const includedAmenities = [
  { icon: Umbrella, text: "Covered cabana with seating" },
  { icon: UtensilsCrossed, text: "Charcoal grill & picnic tables" },
  { icon: Car, text: "Dedicated parking spot" },
  { icon: Waves, text: "Private river access" },
  { icon: ShowerHead, text: "Clean restrooms nearby" },
  { icon: Wifi, text: "Free Wi-Fi on property" },
  { icon: TreePine, text: "Shaded riverside location" },
  { icon: Users, text: "Entry for 8" }
];

const rules = [
  { icon: Baby, text: "Family-friendly environment", allowed: true },
  { icon: Check, text: "Quiet hours from 10:30 PM to 7:30 AM", allowed: true },
  { icon: Music, text: "Music at reasonable levels", allowed: true },
  { icon: Info, text: "No outside tubes (rentals available)", allowed: false },
  { icon: Dog, text: "Dogs not allowed", allowed: false },
  { icon: Sparkles, text: "No glass or styrofoam on river", allowed: false }
];

const galleryImages = [
  { src: aerialCabanas, alt: "Aerial view of riverside cabanas" },
  { src: cabanasMultiple, alt: "Multiple thatched-roof cabanas with guests" },
  { src: cabanaSeating, alt: "Cabana seating area with river view" },
  { src: friendsGroupCabana, alt: "Friends group enjoying the cabana" },
  { src: riverHangout, alt: "Family enjoying the river" },
  { src: grillingFood, alt: "Grilling at the cabana" },
  { src: cabanasRiverStairs, alt: "Cabanas with stairs to river access" },
  { src: cabanasFloatingGuests, alt: "Guests floating by the cabanas" },
  { src: kidsRiverTubes, alt: "Kids playing with tubes in the river" },
  { src: tubingRiverCanyon, alt: "Tubing down the river canyon" },
  { src: cabanaInteriorView, alt: "Inside view of the cabana with seating" },
  { src: cabanasRiverViewWide, alt: "Cabanas overlooking the scenic river" },
  { src: cabanaRiverView, alt: "Cabana overlooking the river" },
  { src: womanWithTubes, alt: "Guest by the river with tubes" },
  { src: familyGroupCabana, alt: "Family group at the cabana" },
  { src: tikiLounge, alt: "Tiki lounge area" },
  { src: tentCabanaCombo, alt: "Glamping tent with cabana setup" },
  { src: clearRiver, alt: "Crystal clear Guadalupe River" },
  { src: riverCanopies, alt: "Shaded river canopies" },
  { src: aerialRiverCabanas, alt: "Aerial view of river and cabanas" },
  { src: riverTubingScene, alt: "Guests tubing and swimming on the river" },
  { src: cabanaGrillSeating, alt: "Cabana with grill and outdoor seating" },
  { src: cabanaInteriorRiverside, alt: "Inside cabana view with river backdrop" }
];

const faqs = [
  {
    question: "What time can I arrive and leave?",
    answer: "Daytime cabana rentals run from 10:30 AM to 6:30 PM. We recommend arriving early to maximize your river time!"
  },
  {
    question: "How many people can fit in a cabana?",
    answer: "Each cabana accommodates up to 12 guests comfortably. For larger groups, you can book multiple adjacent cabanas. Entry for 8 is included with cabana rental. You can pay for additional people and parking at time of booking or on site when you arrive."
  },
  {
    question: "Do I need to bring my own tubes?",
    answer: "Outside tubes are not allowed on the property, but we have tube rentals available for purchase. This helps us maintain the quality and safety of equipment on the river."
  },
  {
    question: "Is food included?",
    answer: "Food is not included, but each cabana has a charcoal grill and picnic tables. Bring your own food and drinks — just no glass containers allowed."
  },
  {
    question: "Is alcohol allowed?",
    answer: "Yes, you may bring your own alcohol, but remember no glass or styrofoam on the river. Management reserves the right to remove guests whose intoxication interferes with the safety or comfort of others."
  },
  {
    question: "Are pets allowed?",
    answer: "No, we love dogs but do not allow them on property. We want everyone to have a fun and relaxing time. I'm sure your dog is great but someone else's dog barking all night and keeping whole campground awake at night or being aggressive towards others is why we do not allow them."
  },
  {
    question: "What if it rains?",
    answer: "Light rain doesn't stop the fun! If there's severe weather (lightning, flash flooding), we'll work with you on rescheduling or provide a full or partial refund. See our cancellation policy for details."
  }
];

const Cabanas = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Guadalupe River Cabana Rentals, New Braunfels | Son's"
        description="Private cabana rentals on the Guadalupe River near New Braunfels. Shaded cabanas with picnic tables, BBQ pits & river access. Starting at $69."
        canonical="/cabanas"
        schemaJson={productSchema(
          "Guadalupe River Cabana Rental",
          "Private riverside cabana rental near New Braunfels, TX with shaded seating, picnic table, river access, and parking.",
          69,
          "https://sonsguadalupe.com/cabanas",
        )}
      />
      <BookingPopupModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={cabanaHero}
            alt="Family enjoying a private cabana on the Guadalupe River"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 via-charcoal/50 to-transparent" />
        </div>
        
        <div className="relative z-10 container-narrow mx-auto px-4 md:px-8 pt-32 pb-16">
          <div className="max-w-2xl">
            <p className="text-coral-orange font-medium mb-4 text-lg">Daytime Rentals</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Your Private Basecamp on the Guadalupe
            </h1>
            <p className="text-white/90 text-lg md:text-xl mb-8">
              Float back to your own shaded home base — not a hot parking lot. Grill, relax, and enjoy the river all day with your own private cabana or in-water picnic table.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="hero" size="xl" onClick={() => setBookingOpen(true)}>
                Book Your Cabana — Starting at $69
              </Button>
              <Link to="/book-now">
                <Button variant="hero-outline" size="xl">
                  View Availability
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cabanas Section */}
      <section id="cabanas" className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-coral-orange font-medium mb-2">Private Riverside Cabanas</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Everything You Need for a Perfect River Day
            </h2>
            <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
              Each cabana comes fully equipped so you can focus on making memories, not logistics.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cabanaFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-elevated transition-shadow"
              >
                <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-teal" />
                </div>
                <h3 className="font-heading text-xl font-bold text-charcoal mb-2">
                  {feature.title}
                </h3>
                <p className="text-charcoal/70">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-teal text-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                What's Included With Your Cabana
              </h2>
              <p className="text-white/80 text-lg mb-8">
                No hidden fees, no surprises. Your cabana rental includes everything you need for a stress-free day on the river.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {includedAmenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <amenity.icon className="w-5 h-5 text-coral-orange" />
                    </div>
                    <span className="text-white/90">{amenity.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Button variant="hero" size="lg" onClick={() => setBookingOpen(true)}>
                  Book Now — Starting at $69
                </Button>
                <p 
                  className="text-coral-orange font-semibold mt-3"
                  style={{ textShadow: '-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white' }}
                >
                  Get 25% OFF Tubing with a Cabana Rental
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src={whatsIncludedImage}
                alt="Riverside cabana with seating and tables"
                className="rounded-2xl shadow-elevated"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              See What Awaits You
            </h2>
            <p className="text-charcoal/70 text-lg max-w-3xl mx-auto">
              Tube, swim, fish, play cornhole, BBQ, or simply relax in the river or under the shade of your cabana. Everyone is sure to have a great time at Son's Guadalupe!
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                className={`relative overflow-hidden rounded-xl ${
                  index === 0 || index === 15 ? 'col-span-2 row-span-2' : ''
                }`}
              >
                <img
                  src={image.src}
                  loading="lazy"
                  alt={image.alt}
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In-Water Picnic Tables Section */}
      <section id="picnic-tables" className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-coral-orange font-medium mb-2">In-Water Covered Picnic Tables</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6">
                Cool Off in the Water!
              </h2>
              <p className="text-charcoal/70 text-lg mb-4">
                Our unique in-water covered picnic tables let you enjoy a full day right in the refreshing Guadalupe River! 
                It's the perfect way to beat the Texas heat and even have a picnic too!
              </p>
              <p className="text-charcoal/70 text-lg mb-6 font-medium">
                In-water picnic tables include entry for 6 and parking for 1 car.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Waves className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <h4 className="font-bold text-charcoal">Sit Right in the River</h4>
                    <p className="text-charcoal/70 text-sm">Tables positioned in shallow water so you can cool your feet while you eat</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Umbrella className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <h4 className="font-bold text-charcoal">Shaded & Covered</h4>
                    <p className="text-charcoal/70 text-sm">Large canopies provide shade so you stay cool all day</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <h4 className="font-bold text-charcoal">Perfect for Small Groups</h4>
                    <p className="text-charcoal/70 text-sm">Great for couples, small families, or a group of friends</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-cream rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-2xl font-bold text-charcoal">$49</p>
                    <p className="text-charcoal/70 text-sm">Starting price</p>
                  </div>
                  <div className="text-right">
                    <p className="text-charcoal font-medium">10:30 AM - 6:30 PM</p>
                    <p className="text-charcoal/70 text-sm">All-day access</p>
                  </div>
                </div>
                <Button variant="default" size="lg" className="w-full" onClick={() => setBookingOpen(true)}>
                  Book a Picnic Table
                </Button>
                <p 
                  className="text-coral-orange font-semibold mt-3 text-center"
                  style={{ textShadow: '-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white' }}
                >
                  Get 25% OFF Tubing with a Picnic Table Rental
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src={riverCanopies}
                alt="In-water covered picnic tables on the Guadalupe River"
                className="rounded-2xl shadow-elevated"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How the Day Flows */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              How the Day Flows
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-teal">1</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-charcoal mb-4">
                Settle Into Your Space
              </h3>
              <p className="text-charcoal/70">
                Arrive, unload, and relax at your private cabana or riverside picnic table. Shade, seating, and a place to call home for the day—no rushing, no setup stress.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-teal">2</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-charcoal mb-4">
                Float When You're Ready
              </h3>
              <p className="text-charcoal/70">
                When the timing feels right, hop on our shuttle upstream and enjoy an easy 2–3 hour float down the Guadalupe. No crowds to fight, no logistics to manage—just a smooth, scenic ride.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-teal">3</span>
              </div>
              <h3 className="font-heading text-xl font-bold text-charcoal mb-4">
                Come Back to Comfort
              </h3>
              <p className="text-charcoal/70">
                Your float ends right where it began. Return to your shaded space, fire up the grill, cool off, and enjoy the rest of the day at your own pace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rules & Policies */}
      <section className="section-padding bg-cream">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Good to Know
              </h2>
              <p className="text-charcoal/70 text-lg">
                A few simple guidelines to ensure everyone has a great time.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <div className="space-y-4">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-center gap-4 py-3 border-b border-border last:border-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      rule.allowed ? 'bg-deep-green/10' : 'bg-coral-orange/10'
                    }`}>
                      {rule.allowed ? (
                        <CheckCircle className="w-5 h-5 text-deep-green" />
                      ) : (
                        <Info className="w-5 h-5 text-coral-orange" />
                      )}
                    </div>
                    <span className="text-charcoal">{rule.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-white">
        <div className="container-narrow mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details 
                key={index}
                className="group bg-cream rounded-xl p-6 cursor-pointer"
              >
                <summary className="flex items-center justify-between font-heading font-bold text-charcoal text-lg list-none">
                  {faq.question}
                  <Sun className="w-5 h-5 text-teal group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-4 text-charcoal/70 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-deep-green text-white">
        <div className="container-narrow mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Reserve Your Riverside Cabana?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Summer weekends fill up fast — only 21 cabanas available. Book now to secure your perfect spot on the Guadalupe.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" size="xl" onClick={() => setBookingOpen(true)}>
              Book Your Cabana Now
            </Button>
            <Button variant="hero-outline" size="xl" className="border-white text-white hover:bg-white/10">
              Call 877-577-7667
            </Button>
          </div>
        </div>
      </section>

      <SEOContentBlock {...seoCopy.cabanas} />
      <Footer />
    </div>
  );
};

export default Cabanas;
